import { FormattedMessage, useIntl } from 'react-intl';
import { Icon, IconName } from '@ets/design-system';

import styles from './ErrorView.module.scss';
import { useEffect } from 'react';
import { navigateToError403, navigateToError404 } from 'router/history';

type ErrorViewProps = {
  onClick?: () => void;
  error: {
    status?: number;
    title?: string;
    message?: string;
  };
};

/**
 * ErrorView
 */
const ErrorView = ({ onClick, error }: ErrorViewProps) => {
  const intl = useIntl();

  useEffect(() => {
    if (onClick) {
      return;
    }
    switch (error.status) {
      case 403: {
        navigateToError403();
        break;
      }

      case 400:
      case 404: {
        navigateToError404();
        break;
      }

      default: {
        navigateToError404();
        break;
      }
    }
  }, [error, onClick]);

  const renderErrorIcon = () => {
    let icon: IconName;

    switch (error.status) {
      case 403:
        icon = 'lock';
        break;

      case 400:
      case 404:
        icon = 'magnifying-glass';
        break;

      default:
        icon = 'magnifying-glass';
        break;
    }

    return <Icon size="12" color="light" icon={icon} testId="error-view" />;
  };

  const renderErrorText = () => {
    let title;
    let subtitle;

    if (error.title && error.message) {
      title = error.title;
      subtitle = error.message;
    } else {
      switch (error.status) {
        case 403:
          title = 'errorView.forbidden.title';
          subtitle = 'errorView.clickToGoBack';
          break;

        case 400:
        case 404:
          title = 'errorView.notFound.title';
          subtitle = 'errorView.clickToGoBack';
          break;

        default:
          title = 'errorView.notFound.title';
          subtitle = 'errorView.clickToGoBack';
          break;
      }
    }

    return (
      <>
        <div className={styles.title} title={intl.formatMessage({ id: title })}>
          <FormattedMessage id={title} />
        </div>
        <div className={styles.subTitle} title={intl.formatMessage({ id: subtitle })}>
          <FormattedMessage id={subtitle} />
        </div>
        {error.message && (
          <div
            className={styles.subTitle}
            title={intl.formatMessage({ id: 'errorView.clickToGoBack' })}
          >
            <FormattedMessage id={'errorView.clickToGoBack'} />
          </div>
        )}
      </>
    );
  };

  if (!onClick) {
    return null;
  }

  return (
    <div className={styles.root} data-testid="error-view-container">
      <div className={`${styles.circle}`} onClick={() => onClick()} data-testid="error-view-circle">
        <div className={`${styles.icon}`}>{renderErrorIcon()}</div>
        {renderErrorText()}
      </div>
    </div>
  );
};

export default ErrorView;
